/**
 * Dashboard sales slice schema (#1339, program #1130 E2)
 *
 * Zod contract for GET /api/v1/dashboard/sales — the «Продажи» tab: how the
 * deals of the period end, the scores of their conversations, the stages the
 * conversations happened at, how many contacts the deals took, and how fresh
 * and complete the CRM data behind all of it is.
 *
 * ONE cohort for the tab: a deal (installation + CRM deal id) with at least one
 * record received in the half-open window `[start, end)`, in the viewer's
 * scope. Its outcome is the deal's CURRENT state in the CRM (the outcome sync
 * snapshot), not «closed in the period» — so the tab has no comparison with a
 * previous window: that window would show today's state of older deals, not
 * their state back then (design 12.10).
 *
 * Nothing here claims a cause: a score next to an outcome, or a stage that
 * moved after a conversation, is a co-occurrence. No amounts, no names, no ids
 * of records or deals — aggregates only.
 */

import { z } from 'zod/v4';
import { lockedDemoOnlySchema } from './common';
import {
  dashboardQualityQuerySchema,
  dashboardQualityScopeSchema,
  dashboardSliceWindowSchema,
} from './dashboard-quality';
import { REPORT_AGENT_MIN_SCORED_CALLS } from './reports';

// ============================================================================
// Thresholds and vocabulary
// ============================================================================

/**
 * An outcome row shows its average score from this many scored conversations
 * on — the reports' minimum, as «few ratings» everywhere else.
 */
export const SALES_MIN_SCORED = REPORT_AGENT_MIN_SCORED_CALLS;
/** A stage shows its share of advanced conversations from this many evaluated ones on. */
export const SALES_MIN_EVALUATED = 5;
/**
 * A conversation's stage is checked this many days after it (the outcome
 * sync's `STAGE_PROGRESSION_WINDOW_DAYS`): a younger one waits for its verdict.
 */
export const SALES_STAGE_CHECK_DAYS = 7;

/** The four outcomes, in display order; every one is answered, empty included. */
export const SALES_OUTCOMES = ['won', 'lost', 'open', 'unknown'] as const;
export type SalesOutcome = (typeof SALES_OUTCOMES)[number];

/**
 * Whether the CRM tells deal outcomes to Aelo:
 * - `on` — an install whose CRM syncs outcomes (Bitrix24, Uspacy since #2086)
 *   and stage-aware scoring on (the sync runs);
 * - `off` — stage-aware scoring is off: no outcomes and no stage verdicts;
 * - `unsupported` — the connected CRM has no outcome sync;
 * - `none` — no CRM is connected.
 */
export const SALES_OUTCOME_SYNC = ['on', 'off', 'unsupported', 'none'] as const;

// ============================================================================
// Query
// ============================================================================

/** The quality slice's query: the same window, zone and demo setting (`projectIds` read with `getAll`). */
export const dashboardSalesQuerySchema = dashboardQualityQuerySchema;

// ============================================================================
// Response
// ============================================================================

const countSchema = z.number().int().nonnegative();
/** Nominally 0–100, not clamped — see `dashboard-quality.ts`. */
const scoreSchema = z.number();

export const dashboardSalesOutcomeRowSchema = z.object({
  outcome: z.enum(SALES_OUTCOMES),
  /** Deals of the period with this outcome; the rows add up to `deals`. */
  deals: countSchema,
  /** Scored conversations of those deals in the window. */
  scoredCount: countSchema,
  /** Their average canonical score (the project's weights); `null` — none scored. */
  score: scoreSchema.nullable(),
});

export const dashboardSalesStageRowSchema = z.object({
  /**
   * The CRM of the stage (vendor id): pipeline and stage ids are portal-local,
   * so two CRMs' stages are never one row, and a name comes only from the
   * reference of the same CRM (#2086). Optional on the wire: an older server
   * does not send it, and a required field would reject the whole answer — a
   * row without it is of an unknown CRM.
   */
  crmVendor: z.string().optional(),
  /** The CRM pipeline of the stage; `null` — not recorded. */
  pipelineId: z.string().nullable(),
  /** The stage the deal was at WHEN the conversation happened. */
  stageId: z.string(),
  /** Conversations of the window at this stage the outcome sync gave a verdict for. */
  evaluated: countSchema,
  /** Of them, the deal moved to a later stage within 7 days after the conversation. */
  advanced: countSchema,
});

export const dashboardSalesDepthStepSchema = z.object({
  /** The contact number (1-based, over the deal's whole life); a range from 6 on. */
  from: z.number().int().positive(),
  /** The range's last number; `null` — open-ended (51 and more). */
  to: z.number().int().positive().nullable(),
  /** Deals whose contact with that number happened in the window. */
  deals: countSchema,
});

export const dashboardSalesResponseSchema = z.object({
  source: z.literal('d1'),
  asOf: z.string().datetime(),
  window: dashboardSliceWindowSchema,
  scope: dashboardQualityScopeSchema,
  /**
   * The demo setting the answer READ: `yes` / `no` as asked, or `only` on a
   * demo-only answer of a locked organization (#2816) — what was read, not
   * what was requested.
   */
  includeDemo: z.enum(['yes', 'no', 'only']),
  demoOnly: lockedDemoOnlySchema,
  crm: z.object({
    /** The CRMs of the organization's active installs (vendor ids, `@aica/core` integrations). */
    vendors: z.array(z.string()),
    outcomeSync: z.enum(SALES_OUTCOME_SYNC),
    /**
     * An active Uspacy install whose call processing is paused or withdrawn:
     * the outcome sync skips it, so it is not counted among the CRMs that sync
     * (`outcomeSync` is `unsupported` when it was the only one). A flag, not a
     * new `outcomeSync` value — an older UI would reject the unknown value and
     * lose the whole answer. Absent — not paused.
     */
    processingPaused: z.boolean().optional(),
  }),
  /** Deals with at least one record received in the window — the tab's denominator. */
  deals: countSchema,
  /** One row per outcome of `SALES_OUTCOMES`, in that order. */
  outcomes: z.array(dashboardSalesOutcomeRowSchema).length(SALES_OUTCOMES.length),
  /** Stages at the time of the conversation, most evaluated first. */
  stages: z.array(dashboardSalesStageRowSchema),
  /**
   * Calls of the window with a stage whose check has not come yet — younger
   * than `SALES_STAGE_CHECK_DAYS`: the card says when their stages appear
   * instead of showing nothing. Optional on the wire — an older server does
   * not send it.
   */
  stagesAwaiting: countSchema.optional(),
  depth: z.object({
    /** Contact numbers 1–5, then 6–10, 11–50, 51+ — only those present. */
    steps: z.array(dashboardSalesDepthStepSchema),
    /** Deals of the period with a contact that took place (not short, not missed) in the window. */
    dealsWithContact: countSchema,
  }),
  freshness: z.object({
    /** The oldest and the newest outcome sync among the deals of the period; `null` — none synced. */
    oldestSyncedAt: z.string().nullable(),
    newestSyncedAt: z.string().nullable(),
    /** Deals of the period with no synced outcome (the `unknown` row). */
    dealsUnsynced: countSchema,
  }),
  coverage: z.object({
    /** Calls of the window that could join a series: took place, not short, not missed. */
    eligibleCalls: countSchema,
    /** Of them, tied to a CRM deal / to a lead / to nothing. */
    linkedDeal: countSchema,
    linkedLead: countSchema,
    unlinked: countSchema,
  }),
});

// ============================================================================
// Types
// ============================================================================

export type DashboardSalesQuery = z.infer<typeof dashboardSalesQuerySchema>;
export type DashboardSalesOutcomeRow = z.infer<typeof dashboardSalesOutcomeRowSchema>;
export type DashboardSalesStageRow = z.infer<typeof dashboardSalesStageRowSchema>;
export type DashboardSalesDepthStep = z.infer<typeof dashboardSalesDepthStepSchema>;
export type DashboardSalesResponse = z.infer<typeof dashboardSalesResponseSchema>;
export type SalesOutcomeSync = (typeof SALES_OUTCOME_SYNC)[number];
